import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
/* eslint-disable camelcase */
import axios from 'axios';
import { useEffect, useState } from 'react';
import {
  Paper,
  TableRow,
  TableCell,
  Container,
  Typography,
  TablePagination,
  Grid,
  Box,
  Card,
  InputAdornment,
  TextField,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import { useNavigate } from 'react-router-dom';
import Scrollbar from '../components/scrollbar';

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.judul.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

// ----------------------------------------------------------------------

export default function BeritaPage() {
  const API_URL = process.env.REACT_APP_API;
  const BACKEND_API = process.env.REACT_APP_BE;
  const navigate = useNavigate();
  const [berita, setBerita] = useState([]);

  const getAllBerita = async () => {
    try {
      await axios.get(`${API_URL}berita/allBerita`)
        .then(({ data }) => { setBerita(data?.data); })
        .catch((err) => { if (err.response.status === 404) { setBerita([]); } });
    } catch (error) { console.log(error); }
  };

  useEffect(() => {
    getAllBerita();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [page, setPage] = useState(0);
  const order = 'asc';
  const orderBy = 'name';
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(9);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };
  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - berita.length) : 0;
  const filteredUsers = applySortFilter(berita, getComparator(order, orderBy), filterName);
  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <>
      <Helmet>
        <title>{localStorage.getItem("judul") !== null ? localStorage.getItem("judul") : ''} | Berita</title>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
        <meta name="description" content="Berita dan informasi seputar gastronomi Bandung Barat" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>

      {/* Hero Banner */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1A1A2E 0%, #2C1810 60%, #3D2314 100%)',
        pt: { xs: '90px', md: '110px' },
        pb: 5,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', top: '10%', right: '5%', width: 300, height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245,166,35,0.12) 0%, transparent 70%)',
          filter: 'blur(50px)', pointerEvents: 'none',
        }} />
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '14px',
              background: 'linear-gradient(135deg, #B85C2C, #F5A623)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <NewspaperOutlinedIcon sx={{ color: '#fff', fontSize: 26 }} />
            </Box>
            <Box>
              <Chip label="Informasi Terkini" sx={{
                background: 'rgba(245,166,35,0.15)', color: '#F5A623',
                fontWeight: 700, fontSize: '11px',
                border: '1px solid rgba(245,166,35,0.3)',
                mb: 0.5,
              }} />
              <Typography variant="h3" sx={{
                color: '#fff', fontWeight: 800,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: { xs: '26px', md: '36px' },
              }}>
                Berita & Informasi
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', ml: 8 }}>
            Temukan berita terbaru seputar gastronomi dan kuliner Bandung Barat
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 5, px: { xs: 2, sm: 3, md: 5 } }}>
        {/* Search Bar */}
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            placeholder="Cari berita..."
            value={filterName}
            onChange={handleFilterByName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#B85C2C' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: '50px',
                background: '#fff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                '& fieldset': { borderColor: 'rgba(184,92,44,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(184,92,44,0.4) !important' },
                '&.Mui-focused fieldset': { borderColor: '#B85C2C !important' },
                px: 1,
              },
            }}
          />
        </Box>

        {/* Results count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography sx={{ color: '#6B7280', fontSize: '14px', fontWeight: 500 }}>
            Menampilkan <strong style={{ color: '#B85C2C' }}>{filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}</strong> dari {filteredUsers.length} berita
          </Typography>
        </Box>

        <Scrollbar>
          <Grid container spacing={3}>
            {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
              const { id, judul, image1, content } = row;
              return (
                <Grid item xs={12} sm={6} md={4} key={id}>
                  <Card
                    onClick={() => navigate(`/berita/${id}`)}
                    elevation={0}
                    sx={{
                      borderRadius: '18px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '1px solid rgba(0,0,0,0.06)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 50px rgba(184,92,44,0.18)',
                        borderColor: 'rgba(184,92,44,0.2)',
                      }
                    }}
                  >
                    {/* Image */}
                    <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                      <img
                        src={`${BACKEND_API}${image1}`}
                        alt={judul}
                        style={{
                          width: '100%', height: '100%', objectFit: 'cover',
                          transition: 'transform 0.5s ease',
                        }}
                      />
                      <Box sx={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.5) 100%)',
                      }} />
                      <Chip label="Berita" size="small" sx={{
                        position: 'absolute', top: 12, left: 12,
                        background: 'rgba(184,92,44,0.9)', color: '#fff',
                        fontWeight: 700, fontSize: '11px', backdropFilter: 'blur(4px)',
                      }} />
                    </Box>
                    {/* Content */}
                    <Box sx={{ p: 2.5 }}>
                      <Typography sx={{
                        fontWeight: 700, fontSize: '15px',
                        color: '#1A1A2E', lineHeight: 1.4, mb: 1.5,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {judul}
                      </Typography>
                      <Typography sx={{
                        fontSize: '13px', color: '#6B7280',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.6,
                      }}>
                        {content?.replace(/<[^>]*>/g, '')?.slice(0, 100)}...
                      </Typography>
                      <Box sx={{
                        mt: 2, display: 'flex', alignItems: 'center', gap: 0.5,
                        color: '#B85C2C', fontWeight: 700, fontSize: '13px',
                      }}>
                        Baca Selengkapnya →
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}

          {isNotFound && (
            <Paper elevation={0} sx={{
              textAlign: 'center', py: 8, px: 4,
              borderRadius: '24px',
              background: '#fff8f0',
              border: '1px solid rgba(184,92,44,0.10)',
              mt: 3,
            }}>
              <SearchIcon sx={{ fontSize: 48, color: '#B85C2C', opacity: 0.4, mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A2E', mb: 1 }}>
                Berita tidak ditemukan
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                Tidak ada hasil untuk &nbsp;
                <strong style={{ color: '#B85C2C' }}>"{filterName}"</strong>.
                <br /> Coba kata kunci lain.
              </Typography>
            </Paper>
          )}
        </Scrollbar>

        <Box sx={{
          mt: 3,
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}>
          <TablePagination
            rowsPerPageOptions={[9, 18, 27]}
            component="div"
            count={berita.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Berita per halaman:"
          />
        </Box>
      </Container>
    </>
  );
}
